export const RESERVED_SUBDOMAINS = new Set([
  "www",
  "api",
  "app",
  "auth",
  "admin",
  "mail",
  "support",
  "billing",
  "status",
  "dashboard",
  "sellsynq",
  "system",
  "dev",
  "staging",
  "test",
  "help",
  "static",
  "assets",
  "cdn",
]);

/**
 * Detect the active tenant subdomain from window.location.
 * Supports:
 * - Subdomain of sellsynq.com (e.g. `wideops.sellsynq.com` -> `wideops`)
 * - Subdomain on localhost (e.g. `wideops.localhost:5173` -> `wideops`)
 * - Query param override for testing (e.g. `?subdomain=wideops` or `?tenant=wideops`)
 */
export function getTenantSubdomain(): string | null {
  if (typeof window === "undefined") return null;

  // 1. Explicit query parameter override (useful for dev and testing on any domain)
  const params = new URLSearchParams(window.location.search);
  const paramSub = params.get("subdomain") || params.get("tenant");
  if (paramSub) {
    const clean = paramSub.trim().toLowerCase();
    if (clean && !RESERVED_SUBDOMAINS.has(clean)) {
      return clean;
    }
  }

  // 2. Hostname-based detection
  const host = window.location.hostname.toLowerCase();

  // Handle sellsynq.com subdomains
  if (host.endsWith(".sellsynq.com")) {
    const sub = host.slice(0, -".sellsynq.com".length);
    if (sub && !RESERVED_SUBDOMAINS.has(sub)) {
      return sub;
    }
    return null;
  }

  // Handle local development with *.localhost (supported by modern browsers)
  if (host.endsWith(".localhost")) {
    const sub = host.slice(0, -".localhost".length);
    if (sub && !RESERVED_SUBDOMAINS.has(sub)) {
      return sub;
    }
    return null;
  }

  return null;
}

/** Converts a company name into a standard URL-safe subdomain slug. */
export function slugifySubdomain(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 30);
}

/** Validate whether a string is a valid subdomain slug. */
export function isValidSubdomain(slug: string): { valid: boolean; error?: string } {
  const clean = slug.trim().toLowerCase();
  if (clean.length < 2) {
    return { valid: false, error: "Subdomain must be at least 2 characters long." };
  }
  if (clean.length > 63) {
    return { valid: false, error: "Subdomain cannot exceed 63 characters." };
  }
  if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(clean)) {
    return {
      valid: false,
      error: "Only lowercase letters, numbers, and hyphens (cannot start or end with a hyphen).",
    };
  }
  if (RESERVED_SUBDOMAINS.has(clean)) {
    return { valid: false, error: `"${clean}" is a reserved system subdomain.` };
  }
  return { valid: true };
}

/** Build full absolute URL to access a company's subdomain workspace. */
export function buildTenantUrl(subdomain: string, path = ""): string {
  if (typeof window === "undefined") return `https://${subdomain}.sellsynq.com${path}`;

  const host = window.location.hostname.toLowerCase();
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : "";

  if (host.includes("localhost") || host === "127.0.0.1") {
    // If browser supports *.localhost resolution
    return `${protocol}//${subdomain}.localhost${port}${path}`;
  }

  return `https://${subdomain}.sellsynq.com${path}`;
}
